import { ChatMessage } from '../data/types'

// Mock knowledge base for Q&A
const KNOWLEDGE_BASE = [
  {
    keywords: ['holiday', 'vacation', 'leave', 'time off'],
    answer:
      'You are entitled to 25 days of paid annual leave per year. Requests should be submitted via the HR portal at least 2 weeks in advance.',
    source: 'Employee Handbook v4.2 - Section 3.1',
  },
  {
    keywords: ['insurance', 'health', 'dental', 'medical'],
    answer:
      'Our comprehensive health insurance is provided by HealthPlus. It covers dental, optical, and general medical. You can add dependents for a small monthly fee.',
    source: 'Benefits Guide 2024',
  },
  {
    keywords: ['promotion', 'career', 'growth', 'raise'],
    answer:
      'Promotions are reviewed bi-annually in June and December. Criteria include performance ratings, peer feedback, and business needs.',
    source: 'Career Progression Framework',
  },
  {
    keywords: ['wfh', 'remote', 'home', 'office'],
    answer:
      'We operate a hybrid model. Employees are expected to be in the office 2 days a week, typically Tuesday and Thursday, unless otherwise agreed with your manager.',
    source: 'Remote Work Policy',
  },
]

// Keywords for KPI mapping
const KPI_KEYWORDS: Record<string, string[]> = {
  'Workload & Burnout': [
    'tired',
    'exhausted',
    'overtime',
    'late',
    'stress',
    'burnout',
    'too much',
    'heavy',
    'busy',
  ],
  'Leadership & Management': [
    'manager',
    'boss',
    'leader',
    'direction',
    'guidance',
    'support',
    'micro',
    'management',
  ],
  Communication: [
    'clear',
    'confusing',
    'info',
    'news',
    'update',
    'email',
    'slack',
    'meeting',
    'communication',
  ],
  'Compensation & Benefits': [
    'pay',
    'salary',
    'money',
    'bonus',
    'raise',
    'insurance',
    'benefits',
    '401k',
  ],
  'Processes & Tools': [
    'slow',
    'broken',
    'tool',
    'software',
    'laptop',
    'vpn',
    'process',
    'bureaucracy',
    'jira',
  ],
}

export const simulateAIResponse = async (
  userMessage: string,
): Promise<ChatMessage> => {
  // Simulate network delay
  await new Promise((resolve) => setTimeout(resolve, 1200))

  const lowerMsg = userMessage.toLowerCase()

  // 1. Detect if it's a question
  const questionWords = [
    'how',
    'what',
    'where',
    'when',
    'why',
    'can',
    'do',
    'does',
    'is',
    'are',
    '?',
  ]
  const isQuestion = questionWords.some(
    (w) => lowerMsg.startsWith(w) || lowerMsg.includes('?'),
  )

  if (isQuestion) {
    // Search KB
    const hit = KNOWLEDGE_BASE.find((item) =>
      item.keywords.some((k) => lowerMsg.includes(k)),
    )

    if (hit) {
      return {
        id: Date.now().toString(),
        role: 'ai',
        content: hit.answer,
        timestamp: new Date(),
        sources: [hit.source],
        type: 'question',
      }
    }

    return {
      id: Date.now().toString(),
      role: 'ai',
      content:
        "I don't have a specific document for that yet, but I've logged your query for the HR team to update the Knowledge Base.",
      timestamp: new Date(),
      type: 'question',
    }
  }

  // 2. Treat as feedback
  // Determine KPI bucket (mock logic)
  let detectedBucket = 'Other'
  for (const [bucket, keywords] of Object.entries(KPI_KEYWORDS)) {
    if (keywords.some((k) => lowerMsg.includes(k))) {
      detectedBucket = bucket
      break
    }
  }

  return {
    id: Date.now().toString(),
    role: 'ai',
    content:
      'Thank you. Your feedback has been recorded anonymously and categorized for the leadership team.',
    timestamp: new Date(),
    type: 'feedback',
  }
}

export const getInitialChatHistory = (): ChatMessage[] => [
  {
    id: 'init-1',
    role: 'ai',
    content:
      "Hi there. I'm your anonymous organizational health assistant. You can share feedback about your work experience or ask me questions about company policies. Your identity is never shared.",
    timestamp: new Date(Date.now() - 1000 * 60 * 60), // 1 hour ago
  },
]
